import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";

export type WeekCycle = "A" | "B" | "C" | "D";
export type WeekDay =
	| "Monday"
	| "Tuesday"
	| "Wednesday"
	| "Thursday"
	| "Friday"
	| "Saturday"
	| "Sunday";
export type SessionBlock = "AM" | "PM" | "TW";

export interface RegularSessionSurgeon {
	id: number;
	name?: string;
	acronym?: string;
	first_name?: string;
	last_name?: string;
	[key: string]: any;
}

export interface RegularSessionHospital {
	id: number;
	name?: string;
	acronym?: string;
	hospital_name?: string;
	[key: string]: any;
}

export interface RegularSession {
	id: number;
	user_id: number;
	surgeon_id: number;
	hospital_id: number;
	week_cycle: WeekCycle;
	week_day: WeekDay;
	session_block: SessionBlock;
	start_date: string | null;
	end_date: string | null;
	start_time: string;
	end_time: string;
	created_at?: string;
	updated_at?: string;
	surgeon?: RegularSessionSurgeon;
	hospital?: RegularSessionHospital;
}

export interface RegularSessionsState {
	items: RegularSession[];
	loading: boolean;
	error: string | null;
}

const initialState: RegularSessionsState = {
	items: [],
	loading: false,
	error: null,
};

export const regularSessionsSlice = createSlice({
	name: "regularSessions",
	initialState,
	reducers: {
		setRegularSessionsLoading: (state, action: PayloadAction<boolean>) => {
			console.log(
				"[regularSessionsSlice] setRegularSessionsLoading:",
				action.payload,
			);
			state.loading = action.payload;
		},

		setRegularSessionsError: (
			state,
			action: PayloadAction<string | null>,
		) => {
			console.log(
				"[regularSessionsSlice] setRegularSessionsError:",
				action.payload,
			);
			state.error = action.payload;
		},

		setRegularSessions: (
			state,
			action: PayloadAction<RegularSession[]>,
		) => {
			console.log(
				"[regularSessionsSlice] setRegularSessions count:",
				action.payload.length,
			);
			state.items = action.payload;
		},

		addRegularSession: (state, action: PayloadAction<RegularSession>) => {
			console.log(
				"[regularSessionsSlice] addRegularSession id:",
				action.payload.id,
			);
			state.items.push(action.payload);
		},

		updateRegularSession: (
			state,
			action: PayloadAction<{
				id: number;
				updates: Partial<RegularSession>;
			}>,
		) => {
			console.log(
				"[regularSessionsSlice] updateRegularSession payload:",
				action.payload,
			);

			const { id, updates } = action.payload;
			const existing = state.items.find((item) => item.id === id);

			if (existing) {
				Object.assign(existing, updates);
				console.log(
					"[regularSessionsSlice] updateRegularSession success for id:",
					id,
				);
			} else {
				console.log(
					"[regularSessionsSlice] updateRegularSession could not find id:",
					id,
				);
			}
		},

		removeRegularSession: (state, action: PayloadAction<number>) => {
			console.log(
				"[regularSessionsSlice] removeRegularSession id:",
				action.payload,
			);
			state.items = state.items.filter(
				(item) => item.id !== action.payload,
			);
		},

		clearRegularSessions: (state) => {
			console.log("[regularSessionsSlice] clearRegularSessions");
			state.items = [];
			state.loading = false;
			state.error = null;
		},
	},
});

export const {
	setRegularSessionsLoading,
	setRegularSessionsError,
	setRegularSessions,
	addRegularSession,
	updateRegularSession,
	removeRegularSession,
	clearRegularSessions,
} = regularSessionsSlice.actions;

// Selectors
export const selectRegularSessions = (state: RootState) =>
	state.regularSessions.items;

export const selectRegularSessionsLoading = (state: RootState) =>
	state.regularSessions.loading;

export const selectRegularSessionsError = (state: RootState) =>
	state.regularSessions.error;

export default regularSessionsSlice.reducer;
